import { Typography } from "@mui/material";
import { ReactElement, ReactNode, useEffect, useRef, useState } from "react";

import useBrowser from "hooks/useBrowser";

import useStyles from "./styles";

type Props = {
  classes?: string;
  collapsible?: boolean;
  collapseText?: string;
  expandable?: boolean;
  expandText?: string;
  maxLines?: string | number;
  buttonBackgroundColor?: string;
  children: ReactElement;
  customExpandButton?: ReactNode;
};

export default function Truncate({
  buttonBackgroundColor = "#fff",
  children,
  classes: classesProp = "",
  collapseText = "...see less",
  collapsible = true,
  customExpandButton,
  expandable = true,
  expandText = "...see more",
  maxLines = 3,
}: Props) {
  const classes = useStyles();

  const { isiOS, isSafari } = useBrowser();

  const wrapperRef = useRef(null);
  const [showReadMore, setShowReadMore] = useState(false);
  const [readMoreOpen, setReadMoreOpen] = useState(false);

  const showExpandButton = expandable && showReadMore && !readMoreOpen;
  const showCollapseButton =
    expandable && collapsible && showReadMore && readMoreOpen;

  useEffect(() => {
    if (
      wrapperRef?.current?.offsetHeight < wrapperRef?.current?.scrollHeight ||
      wrapperRef?.current?.offsetWidth < wrapperRef?.current?.scrollWidth
    ) {
      setShowReadMore(true);
    } else {
      setShowReadMore(false);
    }
  }, [children, wrapperRef]);

  if (isiOS || isSafari) {
    return children;
  }

  return (
    <div
      ref={wrapperRef}
      className={`${classes.wrapper}${classesProp ? ` ${classesProp}` : ""}`}
      style={{
        WebkitLineClamp: !readMoreOpen ? `${maxLines}` : "initial",
      }}
    >
      {children}

      {showExpandButton && customExpandButton && (
        <div className={classes.expandButton}>{customExpandButton}</div>
      )}

      {showExpandButton && !customExpandButton && (
        <button
          className={`${classes.button} ${classes.expandButton}`}
          style={{ backgroundColor: buttonBackgroundColor }}
          type="button"
          onClick={() => setReadMoreOpen(true)}
          onKeyUp={(event) => {
            if (event.key === "Enter") {
              setReadMoreOpen(true);
            }
          }}
        >
          <Typography color="textSecondary" component="span" variant="body2">
            {expandText}
          </Typography>
        </button>
      )}

      {showCollapseButton && (
        <button
          className={`${classes.button}`}
          style={{ backgroundColor: buttonBackgroundColor }}
          type="button"
          onClick={() => setReadMoreOpen(false)}
          onKeyUp={(event) => {
            if (event.key === "Enter") {
              setReadMoreOpen(false);
            }
          }}
        >
          <Typography color="textSecondary" component="span" variant="body2">
            {collapseText}
          </Typography>
        </button>
      )}
    </div>
  );
}
